import { useState } from "react";

import { isEmpty } from "lodash-es";

import { GenericObject } from "src/api/flowTypes";
import { ColorIcon } from "src/design-system/ColorIcon";
import {
  type AdditionalColor,
  COLOR_HEX,
  type ColorIconColor,
} from "src/design-system/utils/colorPalette";
import { POSSIBLE_DECISION_STATUS_CODES } from "src/flow/decisionHistory/SharedStatusColumn";
import { Card } from "src/performance/Card";
import { CardDropdown } from "src/performance/CardDropdown";
import { ErrorOverlay } from "src/performance/ErrorOverlay";
import {
  DIVIDER as LEGEND_DIVIDER,
  Legend,
  type LegendItem,
} from "src/performance/Legend";
import { NoDataOverlay } from "src/performance/NoDataOverlay";
import { NoErrorsOverlay } from "src/performance/NoErrorsOverlay";
import { barY, layer, lineY, tooltip } from "src/performance/Plot/marks";
import { Plot } from "src/performance/Plot/Plot";
import { useErrors } from "src/performance/queries";
import { ErrorsResult, PerformanceFilters } from "src/performance/types";
import { useFlowContext } from "src/router/routerContextHooks";
import { DECISION_ERROR_STATUS_CODES } from "src/utils/constants";
import { formatNumber } from "src/utils/numbers";

const COLORS: Record<number, AdditionalColor> = {
  500: "red-700",
  424: "yellow-400",
  422: "red-500",
  409: "red-200",
  408: "orange-500",
};

const UNEXPECTED_ERROR_COLOR: AdditionalColor = "gray-200";

const getErrorColor = (code: string): ColorIconColor =>
  code in COLORS
    ? COLORS[code as unknown as keyof typeof COLORS]
    : UNEXPECTED_ERROR_COLOR;

const getErrorTypeLabel = (code: string) => {
  const found = POSSIBLE_DECISION_STATUS_CODES.find(
    (entry) => entry.key === code,
  );
  return found ? found.value : `Error code ${code}`;
};

const NO_DATA_STATE = { by_error: [], error_rate: [] };

//get dynamic legend items based on selected error code filter and data
const getDynamicLegendItems = (
  errorCodeFilter: string,
  errorsData: ErrorsResult | undefined,
): LegendItem[] => {
  if (errorCodeFilter !== "all") {
    // Show only filtered error type + error rate
    return [
      {
        color: getErrorColor(errorCodeFilter),
        name: getErrorTypeLabel(errorCodeFilter),
        type: "circle",
      },
      LEGEND_DIVIDER,
      { color: "gray-600", name: "Error rate", type: "dash" },
    ];
  }

  // Show only error types present in data if 'all' is selected
  const codesInData = [
    ...new Set(errorsData?.by_error.map((d) => d.code) || []),
  ];
  return [
    ...codesInData.map<LegendItem>((code) => ({
      color: getErrorColor(code),
      name: getErrorTypeLabel(code),
      type: "circle",
    })),
    LEGEND_DIVIDER,
    { color: "gray-600", name: "Error rate", type: "dash" },
  ];
};

export const ErrorsChart = ({ filters }: { filters: PerformanceFilters }) => {
  const { workspace } = useFlowContext();
  const [errorCodeFilter, setErrorCodeFilter] = useState<string>("all");
  const errors = useErrors(workspace.base_url, {
    ...filters,
    errorCodes: errorCodeFilter === "all" ? [] : [errorCodeFilter],
  });

  const dynamicLegendItems = getDynamicLegendItems(
    errorCodeFilter,
    errors.data,
  );

  return (
    <Card>
      <Card.Header>
        Error rate over time
        <CardDropdown
          elements={[
            { key: "all", value: "All error types" },
            ...DECISION_ERROR_STATUS_CODES.map((errorCode) => ({
              key: errorCode,
              value: `${getErrorTypeLabel(errorCode)}`,
            })),
          ]}
          renderValue={(item) => (
            <div className="flex items-center gap-x-1.5">
              <ColorIcon color={getErrorColor(item.key)} size="3xs" />
              {item.value}
            </div>
          )}
          selected={errorCodeFilter}
          width="w-40"
          onSelect={setErrorCodeFilter}
        />
      </Card.Header>
      <Card.Body>
        {errors.isError && <ErrorOverlay onRetry={errors.refetch} />}
        {errors.isSuccess &&
          isEmpty(errors.data?.by_error) &&
          isEmpty(errors.data?.error_rate) && <NoDataOverlay />}
        {errors.data?.error_rate.length &&
          errors.data.error_rate.every((bin) => bin.value === 0) && (
            <NoErrorsOverlay />
          )}
        <Plot
          data={errors.data ?? NO_DATA_STATE}
          isLoading={errors.isFetching}
          marks={[
            barY(
              {
                x: "bin",
                y: "value",
                fill: (d) => COLOR_HEX[getErrorColor(d.code)],
              },
              { select: (d) => (d as GenericObject).by_error },
            ),
            layer([
              lineY(
                {
                  x: "bin",
                  y: "value",
                  stroke: COLOR_HEX["gray-600"],
                },
                {
                  select: (d) => (d as GenericObject).error_rate,
                },
              ),
              tooltip(
                {
                  x: "bin",
                  y: "value",
                  title: (d) =>
                    `Error rate: ${formatNumber(d.value, {
                      style: "percent",
                      maximumFractionDigits: 1,
                    })}`,
                  anchor: "top",
                },
                {
                  select: (d) => (d as GenericObject).error_rate,
                },
              ),
            ]),
            tooltip(
              {
                x: "bin",
                y: "value",
                title: (d) => `${getErrorTypeLabel(d.code)}: ${d.value}`,
              },
              { select: (d) => (d as GenericObject).by_error, stacked: true },
            ),
          ]}
          timeWindow={filters.timeWindow}
        />
        <Legend items={dynamicLegendItems} />
      </Card.Body>
    </Card>
  );
};
